import { FC, type JSX } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { useFormatter } from '@eneco/i18n';
import { NotificationBox } from '@eneco/sparky';
import { Products_Offers_V3_OfferUsagesResponseModel } from '@monorepo-types/dc';
import { NotificationField } from '@sitecore/types/OfferStep';

import { FlowContextSme } from '../../context';

interface Props {
  flowContext: FlowContextSme;
  usageData?: Products_Offers_V3_OfferUsagesResponseModel;
  redeliveryMoreNotificationTextualData?: NotificationField;
  redeliveryLessNotificationTextualData?: NotificationField;
}

export const PriceDetailsOverviewRedeliveryNotification: FC<Props> = ({
  flowContext,
  usageData,
  redeliveryMoreNotificationTextualData,
  redeliveryLessNotificationTextualData,
}): JSX.Element => {
  const { hasSolarPanels } = flowContext;

  const {
    electricityDeliveryNormal = 0,
    electricityDeliveryLow = 0,
    electricityRedeliveryNormal = 0,
    electricityRedeliveryLow = 0,
    electricityRedeliveryRemainder = 0,
  } = usageData || {};

  const { format, number } = useFormatter();

  const electricityTotal = Number(electricityDeliveryNormal) + Number(electricityDeliveryLow);
  const electricityRedeliveryTotal = electricityRedeliveryNormal + electricityRedeliveryLow;

  const shouldShowNotificationRedeliveryMoreThanUsage =
    !!hasSolarPanels &&
    electricityRedeliveryTotal > electricityTotal &&
    !!redeliveryMoreNotificationTextualData?.value?.content;
  const shouldShowNotificationRedeliveryLessThanUsage =
    !!hasSolarPanels &&
    electricityRedeliveryTotal <= electricityTotal &&
    !!redeliveryLessNotificationTextualData?.value?.content;

  return (
    <>
      {shouldShowNotificationRedeliveryMoreThanUsage && (
        <NotificationBox
          isAlert={false}
          variant={redeliveryMoreNotificationTextualData?.value?.variant}
          title={redeliveryMoreNotificationTextualData?.value?.title}
          text={
            <RichText
              html={format(redeliveryMoreNotificationTextualData?.value?.content ?? '', {
                electricityTotal: String(number.noFractionDigits(electricityTotal)),
                electricityRedelivery: String(number.noFractionDigits(electricityRedeliveryTotal)),
                electricityRedeliveryRemainder: String(number.noFractionDigits(electricityRedeliveryRemainder ?? 0)),
              })}
            />
          }
        />
      )}

      {shouldShowNotificationRedeliveryLessThanUsage && (
        <NotificationBox
          isAlert={false}
          variant={redeliveryLessNotificationTextualData?.value?.variant}
          title={redeliveryLessNotificationTextualData?.value?.title}
          text={
            <RichText
              html={format(redeliveryLessNotificationTextualData?.value?.content ?? '', {
                electricityTotal: String(number.noFractionDigits(electricityTotal)),
                electricityRedelivery: String(number.noFractionDigits(electricityRedeliveryTotal)),
              })}
            />
          }
        />
      )}
    </>
  );
};
